import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingCart, Heart, Menu, Phone, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useSettings } from "@/context/SettingsContext";
import { STORE, waLink, assetUrl } from "@/config";
import {
  Sheet, SheetContent, SheetTrigger,
} from "@/components/ui/sheet";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Categories", to: "/categories" },
  { label: "Track", to: "/track" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const { cartCount, wishlist, setCartOpen } = useStore();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/shop?search=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-40">
      {/* announcement */}
      <div className="bg-[#1C120C] text-[#FDE68A] text-[11px] sm:text-xs overflow-hidden">
        <div className="whitespace-nowrap py-2 marquee flex gap-12 font-medium tracking-wide">
          {[0, 1].map((k) => (
            <span key={k} className="flex gap-12">
              <span>✦ FREE Pan-India shipping above ₹1500</span>
              <span>✦ 100% Authentic Kashmiri Dry Fruits</span>
              <span>✦ Freshly packed & hygienically sealed</span>
              <span>✦ Direct from Kashmir to your door</span>
            </span>
          ))}
        </div>
      </div>

      <div className={`bg-white/90 backdrop-blur border-b border-[#E7E0D6] transition-shadow ${scrolled ? "shadow-md" : ""}`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex items-center gap-4 h-16 sm:h-20">
            {/* mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden text-[#1F130B]" data-testid="mobile-menu-btn"><Menu size={24} /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 bg-[#FAF6F0] p-0">
                <div className="p-5 border-b border-[#E7E0D6]">
                  <p className="font-display text-lg font-bold text-[#1F130B]">SONIA<span className="text-[#C2410C]"> DRY FRUITS</span></p>
                  <p className="text-xs text-[#6B5B52]">Jammu · Kashmir</p>
                </div>
                <nav className="flex flex-col p-3">
                  {NAV.map((n) => (
                    <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className="px-3 py-3 rounded-lg text-[#1F130B] font-medium hover:bg-white" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</Link>
                  ))}
                  <Link to="/faq" onClick={() => setMobileOpen(false)} className="px-3 py-3 rounded-lg text-[#1F130B] font-medium hover:bg-white">FAQ</Link>
                </nav>
              </SheetContent>
            </Sheet>

            <Link to="/" className="shrink-0 flex items-center gap-2.5" data-testid="logo-link">
              <img src={assetUrl(settings.logo)} alt="Sonia Dry Fruits Jammu" className="h-11 w-11 sm:h-14 sm:w-14 rounded-full object-cover ring-2 ring-[#D97706]/40" />
              <div>
                <p className="font-display text-base sm:text-xl font-bold leading-none text-[#1F130B]">
                  SONIA<span className="text-[#C2410C]"> DRY FRUITS</span>
                </p>
                <p className="text-[9px] sm:text-[11px] uppercase tracking-[0.3em] text-[#9A3412] mt-0.5">Jammu · Kashmir</p>
              </div>
            </Link>

            {/* desktop nav */}
            <nav className="hidden lg:flex items-center gap-7 ml-6">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors hover:text-[#C2410C] ${location.pathname === n.to ? "text-[#C2410C]" : "text-[#1F130B]"}`}
                >
                  {n.label}
                </Link>
              ))}
            </nav>

            {/* search */}
            <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xs ml-auto">
              <div className="relative w-full">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="search-input"
                  placeholder="Search dry fruits..."
                  className="w-full rounded-full border border-[#E7E0D6] bg-[#FAF6F0] pl-9 pr-4 py-2 text-sm outline-none focus:border-[#C2410C]"
                />
              </div>
            </form>

            <div className="flex items-center gap-1 sm:gap-2 ml-auto md:ml-2">
              <a href={waLink("Hi! I'd like to know more about your dry fruits.")} target="_blank" rel="noreferrer" className="hidden sm:grid h-10 w-10 place-items-center rounded-full hover:bg-[#FEF3C7] text-[#15803D]" data-testid="header-whatsapp" title="WhatsApp"><Phone size={19} /></a>
              <button onClick={() => navigate("/shop?wishlist=1")} className="relative h-10 w-10 grid place-items-center rounded-full hover:bg-[#FEF3C7]" data-testid="wishlist-header-btn">
                <Heart size={20} className="text-[#1F130B]" />
                {wishlist.length > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 w-4 grid place-items-center rounded-full bg-[#C2410C] text-white text-[10px] font-bold">{wishlist.length}</span>}
              </button>
              <button onClick={() => setCartOpen(true)} className="relative h-10 w-10 grid place-items-center rounded-full hover:bg-[#FEF3C7]" data-testid="cart-header-btn">
                <ShoppingCart size={20} className="text-[#1F130B]" />
                {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 h-4 w-4 grid place-items-center rounded-full bg-[#C2410C] text-white text-[10px] font-bold">{cartCount}</span>}
              </button>
            </div>
          </div>

          {/* mobile search */}
          <form onSubmit={submitSearch} className="md:hidden pb-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="mobile-search-input" placeholder="Search dry fruits..." className="w-full rounded-full border border-[#E7E0D6] bg-[#FAF6F0] pl-9 pr-4 py-2 text-sm outline-none focus:border-[#C2410C]" />
            </div>
          </form>
        </div>
      </div>
    </header>
  );
};
